import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, Dialog, Icon, Textarea, Typography, Popover, Progress } from '@equinor/eds-core-react'
import { close, help_outline } from '@equinor/eds-icons'
import { baseFont } from '@/styles/layout'
import { deleteImage, updateImageDescription } from '@/api/apiService'
import { useConfirmDialog, useInfoDialog } from '@/hooks'
import { addSnackBarMessage } from '@/core/store/globalStore'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '@/core/models/enum'
import { VContainer } from '@/styles'

interface ImageProps {
    tag: string
    plant: string
    showImage: boolean
    onClose: () => void
    url: string
    description: string
    docNo: string
}

const tooltipImage = 'Describe the purpose of this image. Max 240 characters.'

const EditImage: React.FC<ImageProps> = ({ tag, plant, showImage, onClose, url, description, docNo }) => {
    const [currentDescription, setCurrentDescription] = useState<string | null>(description)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [isTooltipDescriptionOpen, setTooltipDescriptionIsOpen] = useState(false)
    const descriptionRef = useRef<HTMLDivElement>(null)
    const [onboardedEdited, setImageEdited] = useState(false)

    const queryClient = useQueryClient()

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onConfirm: () => {},
        onReject: () => onClose(),
        autoClose: true,
    })

    const { showConfirm: showConfirmDelete } = useConfirmDialog({
        title: 'Delete image',
        content: 'Are you sure you want to delete this image? This action cannot be undone.',
        confirmButtonTitle: 'Cancel',
        rejectButtonTitle: 'Delete',
        onConfirm: () => {},
        onReject: () => handleDelete(),
        autoClose: true,
        type: 'Error',
    })

    const { showInfo } = useInfoDialog(
        {
            title: "You can't delete this image",
            content: (
                <VContainer $gap="16px">
                    <Typography>Only the user who uploads an image can delete it.</Typography>
                </VContainer>
            ),
            type: 'Info',
        },
        []
    )

    const handleChangeDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target
        setImageEdited(true)
        setCurrentDescription(value)
    }

    const handleClose = async () => {
        if (onboardedEdited) {
            showConfirm()
        } else {
            onClose()
        }
    }

    const handleDeleteButton = async () => {
        showConfirmDelete()
    }

    const handleEdiImageSaveButton = async () => {
        try {
            if (!docNo) {
                return
            }
            setIsSaving(true)
            const response = await updateImageDescription(plant, docNo, currentDescription ? currentDescription : '')
            if (response) {
                setImageEdited(false)
                addSnackBarMessage(`Image description updated`)
                queryClient.invalidateQueries({
                    queryKey: [QueryKeys.TagDetails],
                })
            } else {
                addSnackBarMessage(`Failed to update image description for ` + docNo)
            }
            setIsSaving(false)
        } catch (error) {
            setIsSaving(false)
            console.error('Error updating sensor:', error)
            addSnackBarMessage(`Failed to update image description`)
        }
    }

    const handleDelete = async () => {
        try {
            if (!docNo) {
                return
            }
            setIsSaving(true)
            const response = await deleteImage(plant, docNo)
            if (response) {
                setImageEdited(false)
                addSnackBarMessage(`Image deleted`)
                queryClient.invalidateQueries({
                    queryKey: [QueryKeys.TagDetails],
                })
                setIsSaving(false)
                onClose()
            } else {
                setIsSaving(false)
                showInfo()
                addSnackBarMessage(`Failed to delete image for ` + docNo + `.`)
            }
        } catch (error) {
            setIsSaving(false)
            console.error('Error updating sensor:', error)
            addSnackBarMessage(`Failed to delete image`)
        }
    }

    return (
        <ImageContainer color="black" open={showImage} onClose={handleClose} isDismissable>
            <Header>
                <HeaderLabel>Edit image</HeaderLabel>
                <CloseButton as="Link" onClick={handleClose}>
                    <Icon data={close} size={24} style={{ color: '#007079' }} />
                </CloseButton>
            </Header>

            {url && <ImageView src={url} alt={`Image ${docNo}`} />}

            <StyledTextArea
                label={
                    <RowStyle>
                        <InputStyledLabel $disabled={true}>Image description</InputStyledLabel>
                        <div ref={descriptionRef}>
                            <Button
                                variant="ghost_icon"
                                aria-label="save action"
                                disabled={true}
                                onClick={() => setTooltipDescriptionIsOpen(!isTooltipDescriptionOpen)}
                            >
                                <Icon data={help_outline} size={24}></Icon>
                            </Button>
                        </div>
                    </RowStyle>
                }
                meta={
                    <RowStyle>
                        <InputStyledLabel $disabled={true}>Mandatory</InputStyledLabel>
                    </RowStyle>
                }
                id="UserAddedDescription"
                value={currentDescription ? currentDescription : ''}
                onChange={handleChangeDescription}
                required
                rows={3}
                maxLength={240}
                placeholder={'Add description'}
                disabled={true}
            />
            <RowContainerBottom>
                <Button as="button" onClick={handleDeleteButton} variant="outlined" disabled={isSaving}>
                    <Label>Delete image</Label>
                </Button>
                <Button as="button" onClick={handleEdiImageSaveButton} disabled={true}>
                    {isSaving ? <Progress.Dots /> : <StyledActionLabel>Save description</StyledActionLabel>}
                </Button>
            </RowContainerBottom>
            <Popover
                open={isTooltipDescriptionOpen}
                anchorEl={descriptionRef.current}
                onClose={() => setTooltipDescriptionIsOpen(false)}
                style={{ width: '300px' }}
            >
                <Popover.Header>
                    <Popover.Title>Image description</Popover.Title>
                    <Button
                        style={{
                            height: '32px',
                            width: '32px',
                        }}
                        variant="ghost_icon"
                        aria-label="Close popover"
                        onClick={() => setTooltipDescriptionIsOpen(false)}
                    >
                        <Icon data={close} size={24} />
                    </Button>
                </Popover.Header>
                <Popover.Content>
                    <StyledPopoverContent variant="body_short">{tooltipImage}</StyledPopoverContent>
                </Popover.Content>
            </Popover>
        </ImageContainer>
    )
}

const StyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;
    &:hover {
        color: white;
        background: #007079;
    }
`

const RowContainerBottom = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    padding: 8px;
    margin-bottom: 0;
    margin-right: 8px;
    gap: 16px;
`

const StyledPopoverContent = styled(Typography)`
    max-width: 280px;
    white-space: normal;
    overflow-wrap: anywhere;
`

const Label = styled.span`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`

const ImageView = styled.img`
    max-height: 40vh;
`

const HeaderLabel = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #3d3d3d;
`

const ImageContainer = styled(Dialog)`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 550px;
    max-width: calc(100% - 32px);
    padding: 16px;
    max-height: 80vh;

    @media (max-width: 1024px) {
        overflow: hidden;
    }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 0px;
`

const CloseButton = styled(Button)`
    ${baseFont}
    color: #007079;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        background: white;
    }

    @media (max-width: 1024px) {
        color: #007079;
        &:hover {
            background: white;
        }
    }
`

const InputStyledLabel = styled(Typography)<{ $disabled?: boolean }>`
    ${baseFont};
    color: ${({ $disabled }) => ($disabled ? '#bdbdbd' : '#6f6f6f')};
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`

const StyledTextArea = styled(Textarea)`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    margin-bottom: 0px;
    background: none;

    @media (max-width: 1024px) {
        & > div {
            background: white;
            background-color: white;
        }
    }
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
    background: #ffffff;
    @media (max-width: 1024px) {
        background: none;
    }
    > * {
        margin-right: 0.3rem;
    }
`

export default EditImage
